import {
  ClipboardPipeline
} from "./chunk-DJCMRWX5.js";
import "./chunk-ZFACQRIZ.js";
import "./chunk-2524DH5P.js";
import {
  Enter
} from "./chunk-QHREOC7B.js";
import "./chunk-BHRKVRIW.js";
import {
  Delete
} from "./chunk-ZPQVM7W7.js";
import "./chunk-NICE7ARI.js";
import "./chunk-U5S3T6FN.js";
import "./chunk-GJCFV3G2.js";
import {
  ButtonView
} from "./chunk-R4FN2FPU.js";
import "./chunk-J3DZJUY2.js";
import {
  CKEditorError,
  Command,
  Plugin,
  UpcastWriter,
  first,
  icons,
  toArray,
  uid
} from "./chunk-H3OD3CNY.js";
import "./chunk-624QZG55.js";

// node_modules/@ckeditor/ckeditor5-list/src/list/utils/listwalker.js
var ListWalker = class {
  /**
   * Creates a document list iterator.
   *
   * @param startElement The start list item block element.
   * @param options.direction The iterating direction.
   * @param options.includeSelf Whether start block should be included in the result (if it's matching other criteria).
   * @param options.sameAttributes Additional attributes that must be the same for each block.
   * @param options.sameIndent Whether blocks with the same indent level as the start block should be included
   * in the result.
   * @param options.lowerIndent Whether blocks with a lower indent level than the start block should be included
   * in the result.
   * @param options.higherIndent Whether blocks with a higher indent level than the start block should be included
   * in the result.
   */
  constructor(startElement, options) {
    this._startElement = startElement;
    this._referenceIndent = startElement.getAttribute("listIndent");
    this._isForward = options.direction == "forward";
    this._includeSelf = !!options.includeSelf;
    this._sameAttributes = toArray(options.sameAttributes || []);
    this._sameIndent = !!options.sameIndent;
    this._lowerIndent = !!options.lowerIndent;
    this._higherIndent = !!options.higherIndent;
  }
  /**
   * Performs only first step of iteration and returns the result.
   *
   * @param startElement The start list item block element.
   * @param options.direction The iterating direction.
   * @param options.includeSelf Whether start block should be included in the result (if it's matching other criteria).
   * @param options.sameAttributes Additional attributes that must be the same for each block.
   * @param options.sameIndent Whether blocks with the same indent level as the start block should be included
   * in the result.
   * @param options.lowerIndent Whether blocks with a lower indent level than the start block should be included
   * in the result.
   * @param options.higherIndent Whether blocks with a higher indent level than the start block should be included
   * in the result.
   */
  static first(startElement, options) {
    const walker = new this(startElement, options);
    const iterator = walker[Symbol.iterator]();
    return first(iterator);
  }
  /**
   * Iterable interface.
   */
  *[Symbol.iterator]() {
    const nestedItems = [];
    for (const { node } of iterateSiblingListBlocks(this._getStartNode(), this._isForward ? "forward" : "backward")) {
      const indent = node.getAttribute("listIndent");
      if (indent < this._referenceIndent) {
        if (!this._lowerIndent) {
          break;
        }
        this._referenceIndent = indent;
      } else if (indent > this._referenceIndent) {
        if (!this._higherIndent) {
          continue;
        }
        if (!this._isForward) {
          nestedItems.push(node);
          continue;
        }
      } else {
        if (!this._sameIndent) {
          if (this._higherIndent) {
            if (nestedItems.length) {
              yield* nestedItems;
              nestedItems.length = 0;
            }
            break;
          }
          continue;
        }
        if (this._sameAttributes.some((attr) => node.getAttribute(attr) !== this._startElement.getAttribute(attr))) {
          break;
        }
      }
      if (nestedItems.length) {
        yield* nestedItems;
        nestedItems.length = 0;
      }
      yield node;
    }
  }
  /**
   * Returns the model element to start iterating.
   */
  _getStartNode() {
    if (this._includeSelf) {
      return this._startElement;
    }
    return this._isForward ? this._startElement.nextSibling : this._startElement.previousSibling;
  }
};
function* iterateSiblingListBlocks(node, direction = "forward") {
  const isForward = direction == "forward";
  const previousNodesByIndent = [];
  let previous = null;
  while (isListItemBlock(node)) {
    let previousNodeInList = null;
    if (previous) {
      const nodeIndent = node.getAttribute("listIndent");
      const previousNodeIndent = previous.getAttribute("listIndent");
      if (nodeIndent > previousNodeIndent) {
        previousNodesByIndent[previousNodeIndent] = previous;
      } else if (nodeIndent < previousNodeIndent) {
        previousNodeInList = previousNodesByIndent[nodeIndent];
        previousNodesByIndent.length = nodeIndent;
      } else {
        previousNodeInList = previous;
      }
    }
    yield { node, previous, previousNodeInList };
    previous = node;
    node = isForward ? node.nextSibling : node.previousSibling;
  }
}
var ListBlocksIterable = class {
  /**
   * @param listHead The head element of a list.
   */
  constructor(listHead) {
    this._listHead = listHead;
  }
  /**
   * List blocks iterator.
   *
   * Iterates over all blocks of a list.
   */
  [Symbol.iterator]() {
    return iterateSiblingListBlocks(this._listHead, "forward");
  }
};

// node_modules/@ckeditor/ckeditor5-list/src/list/utils/model.js
var ListItemUid = class {
  /**
   * Returns the next ID.
   *
   * @internal
   */
  /* istanbul ignore next: static function definition -- @preserve */
  static next() {
    return uid();
  }
};
function isListItemBlock(node) {
  return !!node && node.is("element") && node.hasAttribute("listItemId");
}
function getAllListItemBlocks(listItem, options = {}) {
  return [
    ...getListItemBlocks(listItem, { ...options, direction: "backward" }),
    ...getListItemBlocks(listItem, { ...options, direction: "forward" })
  ];
}
function getListItemBlocks(listItem, options = {}) {
  const isForward = options.direction == "forward";
  const items = Array.from(new ListWalker(listItem, {
    ...options,
    includeSelf: isForward,
    sameIndent: true,
    sameAttributes: "listItemId"
  }));
  return isForward ? items : items.reverse();
}
function getNestedListBlocks(listItem) {
  return Array.from(new ListWalker(listItem, {
    direction: "forward",
    higherIndent: true
  }));
}
function getListItems(listItem) {
  const backwardBlocks = new ListWalker(listItem, {
    sameIndent: true,
    sameAttributes: "listType"
  });
  const forwardBlocks = new ListWalker(listItem, {
    sameIndent: true,
    sameAttributes: "listType",
    includeSelf: true,
    direction: "forward"
  });
  return [
    ...Array.from(backwardBlocks).reverse(),
    ...forwardBlocks
  ];
}
function isFirstBlockOfListItem(listBlock) {
  const previousSibling = ListWalker.first(listBlock, {
    sameIndent: true,
    sameAttributes: "listItemId"
  });
  if (!previousSibling) {
    return true;
  }
  return false;
}
function isLastBlockOfListItem(listBlock) {
  const nextSibling = ListWalker.first(listBlock, {
    direction: "forward",
    sameIndent: true,
    sameAttributes: "listItemId"
  });
  if (!nextSibling) {
    return true;
  }
  return false;
}
function expandListBlocksToCompleteItems(blocks, options = {}) {
  blocks = toArray(blocks);
  const higherIndent = options.withNested !== false;
  const allBlocks = /* @__PURE__ */ new Set();
  for (const block of blocks) {
    for (const itemBlock of getAllListItemBlocks(block, { higherIndent })) {
      allBlocks.add(itemBlock);
    }
  }
  return sortBlocks(allBlocks);
}
function expandListBlocksToCompleteList(blocks) {
  blocks = toArray(blocks);
  const allBlocks = /* @__PURE__ */ new Set();
  for (const block of blocks) {
    for (const itemBlock of getListItems(block)) {
      allBlocks.add(itemBlock);
    }
  }
  return sortBlocks(allBlocks);
}
function splitListItemBefore(listBlock, writer) {
  const blocks = getListItemBlocks(listBlock, { direction: "forward" });
  const id = ListItemUid.next();
  for (const block of blocks) {
    writer.setAttribute("listItemId", id, block);
  }
  return blocks;
}
function mergeListItemBefore(listBlock, parentBlock, writer) {
  const attributes = {};
  for (const [key, value] of parentBlock.getAttributes()) {
    if (key.startsWith("list")) {
      attributes[key] = value;
    }
  }
  const blocks = getListItemBlocks(listBlock, { direction: "forward" });
  for (const block of blocks) {
    writer.setAttributes(attributes, block);
  }
  return blocks;
}
function indentBlocks(blocks, writer, { expand, indentBy = 1 } = {}) {
  blocks = toArray(blocks);
  const allBlocks = expand ? expandListBlocksToCompleteItems(blocks) : blocks;
  for (const block of allBlocks) {
    const blockIndent = block.getAttribute("listIndent") + indentBy;
    if (blockIndent < 0) {
      removeListAttributes(block, writer);
    } else {
      writer.setAttribute("listIndent", blockIndent, block);
    }
  }
  return allBlocks;
}
function outdentBlocksWithMerge(blocks, writer) {
  blocks = toArray(blocks);
  const allBlocks = expandListBlocksToCompleteItems(blocks);
  const visited = /* @__PURE__ */ new Set();
  const referenceIndent = Math.min(...allBlocks.map((block) => block.getAttribute("listIndent")));
  const parentBlocks = /* @__PURE__ */ new Map();
  for (const block of allBlocks) {
    parentBlocks.set(block, ListWalker.first(block, { lowerIndent: true }));
  }
  for (const block of allBlocks) {
    if (visited.has(block)) {
      continue;
    }
    visited.add(block);
    const blockIndent = block.getAttribute("listIndent") - 1;
    if (blockIndent < 0) {
      removeListAttributes(block, writer);
      continue;
    }
    if (block.getAttribute("listIndent") == referenceIndent) {
      const mergedBlocks = mergeListItemIfNotLast(block, parentBlocks.get(block), writer);
      for (const mergedBlock of mergedBlocks) {
        visited.add(mergedBlock);
      }
      if (mergedBlocks.length) {
        continue;
      }
    }
    writer.setAttribute("listIndent", blockIndent, block);
  }
  return sortBlocks(visited);
}
function removeListAttributes(blocks, writer) {
  blocks = toArray(blocks);
  for (const block of blocks) {
    if (block.is("element", "listItem")) {
      writer.rename(block, "paragraph");
    }
  }
  for (const block of blocks) {
    for (const attributeKey of block.getAttributeKeys()) {
      if (attributeKey.startsWith("list")) {
        writer.removeAttribute(attributeKey, block);
      }
    }
  }
  return blocks;
}
function isSingleListItem(blocks) {
  if (!blocks.length) {
    return false;
  }
  const firstItemId = blocks[0].getAttribute("listItemId");
  if (!firstItemId) {
    return false;
  }
  return !blocks.some((item) => item.getAttribute("listItemId") != firstItemId);
}
function outdentFollowingItems(lastBlock, writer) {
  const changedBlocks = [];
  let currentIndent = Number.POSITIVE_INFINITY;
  for (const { node } of iterateSiblingListBlocks(lastBlock.nextSibling, "forward")) {
    const indent = node.getAttribute("listIndent");
    if (indent == 0) {
      break;
    }
    if (indent < currentIndent) {
      currentIndent = indent;
    }
    const newIndent = indent - currentIndent;
    writer.setAttribute("listIndent", newIndent, node);
    changedBlocks.push(node);
  }
  return changedBlocks;
}
function sortBlocks(blocks) {
  return Array.from(blocks).filter((block) => block.root.rootName !== "$graveyard").sort((a, b) => a.index - b.index);
}
function getSelectedBlockObject(model) {
  const selectedElement = model.document.selection.getSelectedElement();
  if (!selectedElement) {
    return null;
  }
  if (model.schema.isObject(selectedElement) && model.schema.isBlock(selectedElement)) {
    return selectedElement;
  }
  return null;
}
function canBecomeSimpleListItem(block, schema) {
  return schema.checkChild(block.parent, "listItem") && schema.checkChild(block, "$text") && !schema.isObject(block);
}
function mergeListItemIfNotLast(block, parentBlock, writer) {
  const parentItemBlocks = getListItemBlocks(parentBlock, { direction: "forward" });
  if (parentItemBlocks.pop().index > block.index) {
    return mergeListItemBefore(block, parentBlock, writer);
  }
  return [];
}

// node_modules/@ckeditor/ckeditor5-list/src/list/listindentcommand.js
var ListIndentCommand = class extends Command {
  /**
   * Creates an instance of the command.
   *
   * @param editor The editor instance.
   * @param indentDirection The direction of indent. If it is equal to `backward`, the command
   * will outdent a list item.
   */
  constructor(editor, indentDirection) {
    super(editor);
    this._direction = indentDirection;
  }
  /**
   * @inheritDoc
   */
  refresh() {
    this.isEnabled = this._checkEnabled();
  }
  /**
   * Indents or outdents (depending on the {@link #constructor}'s `indentDirection` parameter) selected list items.
   *
   * @fires execute
   * @fires afterExecute
   */
  execute() {
    const model = this.editor.model;
    const blocks = getSelectedListBlocks(model.document.selection);
    model.change((writer) => {
      const changedBlocks = [];
      if (isSingleListItem(blocks) && !isFirstBlockOfListItem(blocks[0])) {
        if (this._direction == "forward") {
          changedBlocks.push(...indentBlocks(blocks, writer));
        }
        changedBlocks.push(...splitListItemBefore(blocks[0], writer));
      } else {
        if (this._direction == "forward") {
          changedBlocks.push(...indentBlocks(blocks, writer, { expand: true }));
        } else {
          changedBlocks.push(...outdentBlocksWithMerge(blocks, writer));
        }
      }
      for (const block of changedBlocks) {
        if (!block.hasAttribute("listType")) {
          continue;
        }
        const previousItemBlock = ListWalker.first(block, { sameIndent: true });
        if (previousItemBlock) {
          writer.setAttribute("listType", previousItemBlock.getAttribute("listType"), block);
        }
      }
      this._fireAfterExecute(changedBlocks);
    });
  }
  /**
   * Fires the `afterExecute` event.
   *
   * @param changedBlocks The changed list elements.
   */
  _fireAfterExecute(changedBlocks) {
    this.fire("afterExecute", sortBlocks(new Set(changedBlocks)));
  }
  /**
   * Checks whether the command can be enabled in the current context.
   *
   * @returns Whether the command should be enabled.
   */
  _checkEnabled() {
    let blocks = getSelectedListBlocks(this.editor.model.document.selection);
    let firstBlock = blocks[0];
    if (!firstBlock) {
      return false;
    }
    if (this._direction == "backward") {
      return true;
    }
    if (isSingleListItem(blocks) && !isFirstBlockOfListItem(blocks[0])) {
      return true;
    }
    blocks = expandListBlocksToCompleteItems(blocks);
    firstBlock = blocks[0];
    const siblingItem = ListWalker.first(firstBlock, { sameIndent: true });
    if (!siblingItem) {
      return false;
    }
    if (siblingItem.getAttribute("listType") == firstBlock.getAttribute("listType")) {
      return true;
    }
    return false;
  }
};
function getSelectedListBlocks(selection) {
  const blocks = Array.from(selection.getSelectedBlocks());
  const firstNonListBlockIndex = blocks.findIndex((block) => !isListItemBlock(block));
  if (firstNonListBlockIndex != -1) {
    blocks.length = firstNonListBlockIndex;
  }
  return blocks;
}

// node_modules/@ckeditor/ckeditor5-list/src/list/listcommand.js
var ListCommand = class extends Command {
  /**
   * Creates an instance of the command.
   *
   * @param editor The editor instance.
   * @param type List type that will be handled by this command.
   */
  constructor(editor, type) {
    super(editor);
    this.type = type;
  }
  /**
   * @inheritDoc
   */
  refresh() {
    this.value = this._getValue();
    this.isEnabled = this._checkEnabled();
  }
  /**
   * Executes the list command.
   *
   * @fires execute
   * @fires afterExecute
   * @param options Command options.
   * @param options.forceValue If set, it will force the command behavior. If `true`, the command will try to convert the
   * selected items and potentially the neighbor elements to the proper list items. If set to `false` it will convert selected elements
   * to paragraphs. If not set, the command will toggle selected elements to list items or paragraphs, depending on the selection.
   */
  execute(options = {}) {
    const model = this.editor.model;
    const document = model.document;
    const selectedBlockObject = getSelectedBlockObject(model);
    const blocks = Array.from(document.selection.getSelectedBlocks()).filter((block) => model.schema.checkAttribute(block, "listType") || canBecomeSimpleListItem(block, model.schema));
    const turnOff = options.forceValue !== void 0 ? !options.forceValue : this.value;
    model.change((writer) => {
      if (turnOff) {
        const lastBlock = blocks[blocks.length - 1];
        const itemBlocks = getListItemBlocks(lastBlock, { direction: "forward" });
        const changedBlocks = [];
        if (itemBlocks.length > 1) {
          changedBlocks.push(...splitListItemBefore(itemBlocks[1], writer));
        }
        changedBlocks.push(...removeListAttributes(blocks, writer));
        changedBlocks.push(...outdentFollowingItems(lastBlock, writer));
        this._fireAfterExecute(changedBlocks);
      } else if ((selectedBlockObject || document.selection.isCollapsed) && isListItemBlock(blocks[0])) {
        const changedBlocks = getListItems(selectedBlockObject || blocks[0]);
        for (const block of changedBlocks) {
          writer.setAttribute("listType", this.type, block);
        }
        this._fireAfterExecute(changedBlocks);
      } else {
        const changedBlocks = [];
        for (const block of blocks) {
          if (!block.hasAttribute("listType")) {
            if (!block.is("element", "listItem") && canBecomeSimpleListItem(block, model.schema)) {
              writer.rename(block, "listItem");
            }
            writer.setAttributes({
              listIndent: 0,
              listItemId: ListItemUid.next(),
              listType: this.type
            }, block);
            changedBlocks.push(block);
          } else {
            for (const node of expandListBlocksToCompleteItems(block, { withNested: false })) {
              if (node.getAttribute("listType") != this.type) {
                writer.setAttribute("listType", this.type, node);
                changedBlocks.push(node);
              }
            }
          }
        }
        this._fireAfterExecute(changedBlocks);
      }
    });
  }
  /**
   * Fires the `afterExecute` event.
   *
   * @param changedBlocks The changed list elements.
   */
  _fireAfterExecute(changedBlocks) {
    this.fire("afterExecute", sortBlocks(new Set(changedBlocks)));
  }
  /**
   * Checks the command's {@link #value}.
   *
   * @returns The current value.
   */
  _getValue() {
    const selection = this.editor.model.document.selection;
    const blocks = Array.from(selection.getSelectedBlocks());
    if (!blocks.length) {
      return false;
    }
    for (const block of blocks) {
      if (block.getAttribute("listType") != this.type) {
        return false;
      }
    }
    return true;
  }
  /**
   * Checks whether the command can be enabled in the current context.
   *
   * @returns Whether the command should be enabled.
   */
  _checkEnabled() {
    const model = this.editor.model;
    const schema = model.schema;
    const selection = model.document.selection;
    const blocks = Array.from(selection.getSelectedBlocks());
    if (!blocks.length) {
      return false;
    }
    if (this.value) {
      return true;
    }
    for (const block of blocks) {
      if (schema.checkAttribute(block, "listType") || canBecomeSimpleListItem(block, schema)) {
        return true;
      }
    }
    return false;
  }
};

// node_modules/@ckeditor/ckeditor5-list/src/list/listmergecommand.js
var ListMergeCommand = class extends Command {
  /**
   * Creates an instance of the command.
   *
   * @param editor The editor instance.
   * @param direction Whether list item should be merged before or after the selected block.
   */
  constructor(editor, direction) {
    super(editor);
    this._direction = direction;
  }
  /**
   * @inheritDoc
   */
  refresh() {
    this.isEnabled = this._checkEnabled();
  }
  /**
   * Merges list blocks together (depending on the {@link #constructor}'s `direction` parameter).
   *
   * @fires execute
   * @fires afterExecute
   * @param options Command options.
   * @param options.shouldMergeOnBlocksContentLevel When set `true`, merging will be performed together
   * with {@link module:engine/model/model~Model#deleteContent} to get rid of the inline content in the selection or take advantage
   * of the heuristics in `deleteContent()` that helps convert lists into paragraphs in certain cases.
   */
  execute({ shouldMergeOnBlocksContentLevel: shouldMergeOnBlocksContentLevel2 = false } = {}) {
    const model = this.editor.model;
    const selection = model.document.selection;
    const changedBlocks = [];
    model.change((writer) => {
      const { firstElement, lastElement } = this._getMergeSubjectElements(selection, shouldMergeOnBlocksContentLevel2);
      const firstIndent = firstElement.getAttribute("listIndent") || 0;
      const lastIndent = lastElement.getAttribute("listIndent");
      const lastElementId = lastElement.getAttribute("listItemId");
      if (firstIndent != lastIndent) {
        const nestedLastElementBlocks = getNestedListBlocks(lastElement);
        changedBlocks.push(...indentBlocks([lastElement, ...nestedLastElementBlocks], writer, {
          indentBy: firstIndent - lastIndent,
          // If outdenting, the entire sub-tree that follows must be included.
          expand: firstIndent < lastIndent
        }));
      }
      if (shouldMergeOnBlocksContentLevel2) {
        let sel = selection;
        if (selection.isCollapsed) {
          sel = writer.createSelection(writer.createRange(writer.createPositionAt(firstElement, "end"), writer.createPositionAt(lastElement, 0)));
        }
        model.deleteContent(sel, { doNotResetEntireContent: selection.isCollapsed });
        const lastElementAfterDelete = sel.getLastPosition().parent;
        const nextSibling = lastElementAfterDelete.nextSibling;
        changedBlocks.push(lastElementAfterDelete);
        if (nextSibling && nextSibling !== lastElement && nextSibling.getAttribute("listItemId") == lastElementId) {
          changedBlocks.push(...mergeListItemBefore(nextSibling, lastElementAfterDelete, writer));
        }
      } else {
        changedBlocks.push(...mergeListItemBefore(lastElement, firstElement, writer));
      }
      this._fireAfterExecute(changedBlocks);
    });
  }
  /**
   * Fires the `afterExecute` event.
   *
   * @param changedBlocks The changed list elements.
   */
  _fireAfterExecute(changedBlocks) {
    this.fire("afterExecute", sortBlocks(new Set(changedBlocks)));
  }
  /**
   * Checks whether the command can be enabled in the current context.
   *
   * @returns Whether the command should be enabled.
   */
  _checkEnabled() {
    const model = this.editor.model;
    const selection = model.document.selection;
    const selectedBlockObject = getSelectedBlockObject(model);
    if (selection.isCollapsed || selectedBlockObject) {
      const positionParent = selectedBlockObject || selection.getFirstPosition().parent;
      if (!isListItemBlock(positionParent)) {
        return false;
      }
      const siblingNode = this._direction == "backward" ? positionParent.previousSibling : positionParent.nextSibling;
      if (!siblingNode) {
        return false;
      }
      if (isSingleListItem([positionParent, siblingNode])) {
        return false;
      }
    } else {
      const lastPosition = selection.getLastPosition();
      const firstPosition = selection.getFirstPosition();
      if (lastPosition.parent === firstPosition.parent) {
        return false;
      }
      if (!isListItemBlock(lastPosition.parent)) {
        return false;
      }
    }
    return true;
  }
  /**
   * Returns the boundary elements the merge should be executed for. These are not necessarily selection's first
   * and last position parents but sometimes sibling or even further blocks depending on the context.
   *
   * @param selection The selection the merge is executed for.
   * @param shouldMergeOnBlocksContentLevel When `true`, merge is performed together with
   * {@link module:engine/model/model~Model#deleteContent} to remove the inline content within the selection.
   */
  _getMergeSubjectElements(selection, shouldMergeOnBlocksContentLevel2) {
    const model = this.editor.model;
    const selectedBlockObject = getSelectedBlockObject(model);
    let firstElement, lastElement;
    if (selection.isCollapsed || selectedBlockObject) {
      const positionParent = selectedBlockObject || selection.getFirstPosition().parent;
      const isFirstBlock = isFirstBlockOfListItem(positionParent);
      if (this._direction == "backward") {
        lastElement = positionParent;
        if (isFirstBlock && !shouldMergeOnBlocksContentLevel2) {
          firstElement = ListWalker.first(positionParent, { sameIndent: true, lowerIndent: true });
        } else {
          firstElement = positionParent.previousSibling;
        }
      } else {
        firstElement = positionParent;
        lastElement = positionParent.nextSibling;
      }
    } else {
      firstElement = selection.getFirstPosition().parent;
      lastElement = selection.getLastPosition().parent;
    }
    return {
      firstElement,
      lastElement
    };
  }
};

// node_modules/@ckeditor/ckeditor5-list/src/list/listsplitcommand.js
var ListSplitCommand = class extends Command {
  /**
   * Creates an instance of the command.
   *
   * @param editor The editor instance.
   * @param direction Whether list item should be split before or after the selected block.
   */
  constructor(editor, direction) {
    super(editor);
    this._direction = direction;
  }
  /**
   * @inheritDoc
   */
  refresh() {
    this.isEnabled = this._checkEnabled();
  }
  /**
   * Splits the list item at the selection.
   *
   * @fires execute
   * @fires afterExecute
   */
  execute() {
    const editor = this.editor;
    editor.model.change((writer) => {
      const changedBlocks = splitListItemBefore(this._getStartBlock(), writer);
      this._fireAfterExecute(changedBlocks);
    });
  }
  /**
   * Fires the `afterExecute` event.
   *
   * @param changedBlocks The changed list elements.
   */
  _fireAfterExecute(changedBlocks) {
    this.fire("afterExecute", sortBlocks(new Set(changedBlocks)));
  }
  /**
   * Checks whether the command can be enabled in the current context.
   *
   * @returns Whether the command should be enabled.
   */
  _checkEnabled() {
    const selection = this.editor.model.document.selection;
    const block = this._getStartBlock();
    return selection.isCollapsed && isListItemBlock(block) && !isFirstBlockOfListItem(block);
  }
  /**
   * Returns the model element that is the main focus of the command (according to the current selection and command direction).
   */
  _getStartBlock() {
    const doc = this.editor.model.document;
    const positionParent = doc.selection.getFirstPosition().parent;
    return this._direction == "before" ? positionParent : positionParent.nextSibling;
  }
};

// node_modules/@ckeditor/ckeditor5-list/src/list/listutils.js
var ListUtils = class extends Plugin {
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "ListUtils";
  }
  /**
   * Expands the given list of selected blocks to include all the items of the lists they're in.
   *
   * @param blocks The list of selected blocks.
   */
  expandListBlocksToCompleteList(blocks) {
    return expandListBlocksToCompleteList(blocks);
  }
  /**
   * Check if the given block is the first in the list item.
   *
   * @param listBlock The list block element.
   */
  isFirstBlockOfListItem(listBlock) {
    return isFirstBlockOfListItem(listBlock);
  }
  /**
   * Returns true if the given model node is a list item block.
   *
   * @param node A model node.
   */
  isListItemBlock(node) {
    return isListItemBlock(node);
  }
  /**
   * Expands the given list of selected blocks to include the leading and tailing blocks of partially selected list items.
   *
   * @param blocks The list of selected blocks.
   * @param options.withNested Whether should include nested list items.
   */
  expandListBlocksToCompleteItems(blocks, options = {}) {
    return expandListBlocksToCompleteItems(blocks, options);
  }
};

// node_modules/@ckeditor/ckeditor5-list/src/list/utils/view.js
function isListView(viewElement) {
  return viewElement.is("element", "ol") || viewElement.is("element", "ul");
}
function isListItemView(viewElement) {
  return viewElement.is("element", "li");
}
function getIndent(listItem) {
  let indent = 0;
  let parent = listItem.parent;
  while (parent) {
    if (isListItemView(parent)) {
      indent++;
    } else {
      const previousSibling = parent.previousSibling;
      if (previousSibling && isListItemView(previousSibling)) {
        indent++;
      }
    }
    parent = parent.parent;
  }
  return indent;
}
function createListElement(writer, indent, type, id = getViewElementIdForListType(type, indent)) {
  return writer.createAttributeElement(getViewElementNameForListType(type), null, {
    priority: 2 * indent / 100 - 100,
    id
  });
}
function createListItemElement(writer, indent, id) {
  return writer.createAttributeElement("li", null, {
    priority: (2 * indent + 1) / 100 - 100,
    id
  });
}
function getViewElementNameForListType(type) {
  return type == "numbered" ? "ol" : "ul";
}
function getViewElementIdForListType(type, indent) {
  return `list-${type}-${indent}`;
}

// node_modules/@ckeditor/ckeditor5-list/src/list/utils/postfixers.js
function findAndAddListHeadToMap(position, itemToListHead) {
  const previousNode = position.nodeBefore;
  if (!isListItemBlock(previousNode)) {
    const item = position.nodeAfter;
    if (isListItemBlock(item)) {
      itemToListHead.set(item, item);
    }
  } else {
    let listHead = previousNode;
    for (const { node } of iterateSiblingListBlocks(listHead, "backward")) {
      listHead = node;
      if (itemToListHead.has(listHead)) {
        return;
      }
    }
    itemToListHead.set(previousNode, listHead);
  }
}
function fixListIndents(listNodes, writer) {
  let maxIndent = 0;
  let prevIndent = -1;
  let fixBy = null;
  let applied = false;
  for (const { node } of listNodes) {
    const itemIndent = node.getAttribute("listIndent");
    if (itemIndent > maxIndent) {
      let newIndent;
      if (fixBy === null) {
        fixBy = itemIndent - maxIndent;
        newIndent = maxIndent;
      } else {
        if (fixBy > itemIndent) {
          fixBy = itemIndent;
        }
        newIndent = itemIndent - fixBy;
      }
      if (newIndent > prevIndent + 1) {
        newIndent = prevIndent + 1;
      }
      writer.setAttribute("listIndent", newIndent, node);
      applied = true;
      prevIndent = newIndent;
    } else {
      fixBy = null;
      maxIndent = itemIndent + 1;
      prevIndent = itemIndent;
    }
  }
  return applied;
}
function fixListItemIds(listNodes, seenIds, writer) {
  const visited = /* @__PURE__ */ new Set();
  let applied = false;
  for (const { node } of listNodes) {
    if (visited.has(node)) {
      continue;
    }
    let listType = node.getAttribute("listType");
    let listItemId = node.getAttribute("listItemId");
    if (seenIds.has(listItemId)) {
      listItemId = ListItemUid.next();
    }
    seenIds.add(listItemId);
    if (node.is("element", "listItem")) {
      if (node.getAttribute("listItemId") != listItemId) {
        writer.setAttribute("listItemId", listItemId, node);
        applied = true;
      }
      continue;
    }
    for (const block of getListItemBlocks(node, { direction: "forward" })) {
      visited.add(block);
      if (block.getAttribute("listType") != listType) {
        listItemId = ListItemUid.next();
        listType = block.getAttribute("listType");
      }
      if (block.getAttribute("listItemId") != listItemId) {
        writer.setAttribute("listItemId", listItemId, block);
        applied = true;
      }
    }
  }
  return applied;
}

// node_modules/@ckeditor/ckeditor5-list/src/list/converters.js
function listItemUpcastConverter() {
  return (evt, data, conversionApi) => {
    const { writer, schema } = conversionApi;
    if (!data.modelRange) {
      return;
    }
    const items = Array.from(data.modelRange.getItems({ shallow: true })).filter((item) => schema.checkAttribute(item, "listItemId"));
    if (!items.length) {
      return;
    }
    const listItemId = ListItemUid.next();
    const listIndent = getIndent(data.viewItem);
    let listType = data.viewItem.parent && data.viewItem.parent.is("element", "ol") ? "numbered" : "bulleted";
    const firstItemListType = items[0].getAttribute("listType");
    if (firstItemListType) {
      listType = firstItemListType;
    }
    const attributes = {
      listItemId,
      listIndent,
      listType
    };
    for (const item of items) {
      if (!item.hasAttribute("listItemId")) {
        writer.setAttributes(attributes, item);
      }
    }
    if (items.length > 1) {
      if (items[1].getAttribute("listItemId") != attributes.listItemId) {
        conversionApi.keepEmptyElement(items[0]);
      }
    }
  };
}
function listUpcastCleanList() {
  return (evt, data, conversionApi) => {
    if (!conversionApi.consumable.test(data.viewItem, { name: true })) {
      return;
    }
    const viewWriter = new UpcastWriter(data.viewItem.document);
    for (const child of Array.from(data.viewItem.getChildren())) {
      if (!isListItemView(child) && !isListView(child)) {
        viewWriter.remove(child);
      }
    }
  };
}
function reconvertItemsOnDataChange(model, editing, attributeNames, listEditing) {
  return () => {
    const changes = model.document.differ.getChanges();
    const itemsToRefresh = [];
    const itemToListHead = /* @__PURE__ */ new Map();
    const changedItems = /* @__PURE__ */ new Set();
    for (const entry of changes) {
      if (entry.type == "insert" && entry.name != "$text") {
        findAndAddListHeadToMap(entry.position, itemToListHead);
        if (!entry.attributes.has("listItemId")) {
          findAndAddListHeadToMap(entry.position.getShiftedBy(entry.length), itemToListHead);
        } else {
          changedItems.add(entry.position.nodeAfter);
        }
      } else if (entry.type == "remove" && entry.attributes.has("listItemId")) {
        findAndAddListHeadToMap(entry.position, itemToListHead);
      } else if (entry.type == "attribute") {
        const item = entry.range.start.nodeAfter;
        if (attributeNames.includes(entry.attributeKey)) {
          findAndAddListHeadToMap(entry.range.start, itemToListHead);
          if (entry.attributeNewValue === null) {
            findAndAddListHeadToMap(entry.range.start.getShiftedBy(1), itemToListHead);
            if (doesItemBlockRequiresRefresh(item)) {
              itemsToRefresh.push(item);
            }
          } else {
            changedItems.add(item);
          }
        } else if (isListItemBlock(item)) {
          if (doesItemBlockRequiresRefresh(item)) {
            itemsToRefresh.push(item);
          }
        }
      }
    }
    for (const listHead of itemToListHead.values()) {
      itemsToRefresh.push(...collectListItemsToRefresh(listHead, changedItems));
    }
    for (const item of new Set(itemsToRefresh)) {
      editing.reconvertItem(item);
    }
  };
  function collectListItemsToRefresh(listHead, changedItems) {
    const itemsToRefresh = [];
    const visited = /* @__PURE__ */ new Set();
    const stack = [];
    for (const { node, previous } of iterateSiblingListBlocks(listHead, "forward")) {
      if (visited.has(node)) {
        continue;
      }
      const itemIndent = node.getAttribute("listIndent");
      if (previous && itemIndent < previous.getAttribute("listIndent")) {
        stack.length = itemIndent + 1;
      }
      stack[itemIndent] = Object.fromEntries(Array.from(node.getAttributes()).filter(([key]) => attributeNames.includes(key)));
      const blocks = getListItemBlocks(node, { direction: "forward" });
      for (const block of blocks) {
        visited.add(block);
        if (doesItemBlockRequiresRefresh(block, blocks)) {
          itemsToRefresh.push(block);
        } else if (doesItemWrappingRequiresRefresh(block, stack, changedItems)) {
          itemsToRefresh.push(block);
        }
      }
    }
    return itemsToRefresh;
  }
  function doesItemBlockRequiresRefresh(item, blocks) {
    const viewElement = editing.mapper.toViewElement(item);
    if (!viewElement) {
      return false;
    }
    const needsRefresh = listEditing.fire("checkElement", {
      modelElement: item,
      viewElement
    });
    if (needsRefresh) {
      return true;
    }
    if (!item.is("element", "paragraph") && !item.is("element", "listItem")) {
      return false;
    }
    const useBogus = shouldUseBogusParagraph(item, attributeNames, blocks);
    if (useBogus && viewElement.is("element", "p")) {
      return true;
    } else if (!useBogus && viewElement.is("element", "span")) {
      return true;
    }
    return false;
  }
  function doesItemWrappingRequiresRefresh(item, stack, changedItems) {
    if (changedItems.has(item)) {
      return false;
    }
    const viewElement = editing.mapper.toViewElement(item);
    let indent = stack.length - 1;
    for (let element = viewElement.parent; !element.is("editableElement"); element = element.parent) {
      const isListItemElement = isListItemView(element);
      const isListElement = isListView(element);
      if (!isListElement && !isListItemElement) {
        continue;
      }
      const eventName = `checkAttributes:${isListItemElement ? "item" : "list"}`;
      const needsRefresh = listEditing.fire(eventName, {
        viewElement: element,
        modelAttributes: stack[indent]
      });
      if (needsRefresh) {
        break;
      }
      if (isListElement) {
        indent--;
        if (indent < 0) {
          return false;
        }
      }
    }
    return true;
  }
}
function listItemDowncastConverter(attributeNames, strategies, model, { dataPipeline } = {}) {
  const consumer = createAttributesConsumer(attributeNames);
  return (evt, data, conversionApi) => {
    const { writer, mapper, consumable } = conversionApi;
    const listItem = data.item;
    if (!attributeNames.includes(data.attributeKey)) {
      return;
    }
    if (!consumer(listItem, consumable)) {
      return;
    }
    const viewElement = findMappedViewElement(listItem, mapper, model);
    removeCustomMarkerElements(viewElement, writer, mapper);
    unwrapListItemBlock(viewElement, writer);
    const viewRange = insertCustomMarkerElements(listItem, viewElement, strategies, writer, { dataPipeline });
    wrapListItemBlock(listItem, viewRange, strategies, writer);
  };
}
function bogusParagraphCreator(attributeNames, { dataPipeline } = {}) {
  return (modelElement, { writer }) => {
    if (!shouldUseBogusParagraph(modelElement, attributeNames)) {
      return null;
    }
    if (!dataPipeline) {
      return writer.createContainerElement("span", { class: "ck-list-bogus-paragraph" });
    }
    const viewElement = writer.createContainerElement("p");
    writer.setCustomProperty("dataPipeline:transparentRendering", true, viewElement);
    return viewElement;
  };
}
function findMappedViewElement(element, mapper, model) {
  const modelRange = model.createRangeOn(element);
  const viewRange = mapper.toViewRange(modelRange).getTrimmed();
  return viewRange.end.nodeBefore;
}
function removeCustomMarkerElements(viewElement, viewWriter, mapper) {
  while (viewElement.parent.is("attributeElement") && viewElement.parent.getCustomProperty("listItemWrapper")) {
    viewWriter.unwrap(viewWriter.createRangeIn(viewElement.parent), viewElement.parent);
  }
  const viewWalker = viewWriter.createPositionBefore(viewElement).getWalker({ direction: "backward" });
  const markersToRemove = [];
  for (const { item } of viewWalker) {
    if (item.is("element") && mapper.toModelElement(item)) {
      break;
    }
    if (item.is("element") && item.getCustomProperty("listItemMarker")) {
      markersToRemove.push(item);
    }
  }
  for (const marker of markersToRemove) {
    viewWriter.remove(marker);
  }
}
function insertCustomMarkerElements(listItem, viewElement, strategies, writer, { dataPipeline }) {
  let viewRange = writer.createRangeOn(viewElement);
  if (!isFirstBlockOfListItem(listItem)) {
    return viewRange;
  }
  for (const strategy of strategies) {
    if (strategy.scope != "itemMarker") {
      continue;
    }
    const markerElement = strategy.createElement(writer, listItem, { dataPipeline });
    if (!markerElement) {
      continue;
    }
    writer.setCustomProperty("listItemMarker", true, markerElement);
    writer.insert(viewRange.start, markerElement);
    viewRange = writer.createRange(writer.createPositionBefore(markerElement), writer.createPositionAfter(viewElement));
    if (!strategy.createWrapperElement || !strategy.canWrapElement) {
      continue;
    }
    const wrapper = strategy.createWrapperElement(writer, listItem, { dataPipeline });
    writer.setCustomProperty("listItemWrapper", true, wrapper);
    if (strategy.canWrapElement(listItem)) {
      viewRange = writer.wrap(viewRange, wrapper);
    } else {
      viewRange = writer.wrap(writer.createRangeOn(markerElement), wrapper);
      viewRange = writer.createRange(viewRange.start, writer.createPositionAfter(viewElement));
    }
  }
  return viewRange;
}
function unwrapListItemBlock(viewElement, viewWriter) {
  let attributeElement = viewElement.parent;
  while (attributeElement.is("attributeElement") && ["ul", "ol", "li"].includes(attributeElement.name)) {
    const parentElement = attributeElement.parent;
    viewWriter.unwrap(viewWriter.createRangeOn(viewElement), attributeElement);
    attributeElement = parentElement;
  }
}
function wrapListItemBlock(listItem, viewRange, strategies, writer) {
  if (!listItem.hasAttribute("listIndent")) {
    return;
  }
  const listItemIndent = listItem.getAttribute("listIndent");
  let currentListItem = listItem;
  for (let indent = listItemIndent; indent >= 0; indent--) {
    const listItemViewElement = createListItemElement(writer, indent, currentListItem.getAttribute("listItemId"));
    const listViewElement = createListElement(writer, indent, currentListItem.getAttribute("listType"));
    for (const strategy of strategies) {
      if ((strategy.scope == "list" || strategy.scope == "item") && currentListItem.hasAttribute(strategy.attributeName)) {
        strategy.setAttributeOnDowncast(writer, currentListItem.getAttribute(strategy.attributeName), strategy.scope == "list" ? listViewElement : listItemViewElement);
      }
    }
    viewRange = writer.wrap(viewRange, listItemViewElement);
    viewRange = writer.wrap(viewRange, listViewElement);
    if (indent == 0) {
      break;
    }
    currentListItem = ListWalker.first(currentListItem, { lowerIndent: true });
    if (!currentListItem) {
      break;
    }
  }
}
function createAttributesConsumer(attributeNames) {
  return (node, consumable) => {
    const events = [];
    for (const attributeName of attributeNames) {
      if (node.hasAttribute(attributeName)) {
        events.push(`attribute:${attributeName}`);
      }
    }
    if (!events.every((event) => consumable.test(node, event) !== false)) {
      return false;
    }
    events.forEach((event) => consumable.consume(node, event));
    return true;
  };
}
function shouldUseBogusParagraph(item, attributeNames, blocks = getAllListItemBlocks(item)) {
  if (!isListItemBlock(item)) {
    return false;
  }
  for (const attributeKey of item.getAttributeKeys()) {
    if (attributeKey.startsWith("selection:")) {
      continue;
    }
    if (!attributeNames.includes(attributeKey)) {
      return false;
    }
  }
  return blocks.length < 2;
}

// node_modules/@ckeditor/ckeditor5-list/src/list/listediting.js
import "/tmp/build_5d4c22f1/node_modules/@ckeditor/ckeditor5-list/theme/documentlist.css";
import "/tmp/build_5d4c22f1/node_modules/@ckeditor/ckeditor5-list/theme/list.css";
var LIST_BASE_ATTRIBUTES = ["listType", "listIndent", "listItemId"];
var ListEditing = class extends Plugin {
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "ListEditing";
  }
  /**
   * @inheritDoc
   */
  static get requires() {
    return [Enter, Delete, ListUtils, ClipboardPipeline];
  }
  /**
   * @inheritDoc
   */
  constructor(editor) {
    super(editor);
    this._downcastStrategies = [];
    editor.config.define("list.multiBlock", true);
  }
  /**
   * @inheritDoc
   */
  init() {
    const editor = this.editor;
    const model = editor.model;
    const multiBlock = editor.config.get("list.multiBlock");
    if (editor.plugins.has("LegacyListEditing")) {
      throw new CKEditorError("list-feature-conflict", this, { conflictPlugin: "LegacyListEditing" });
    }
    model.schema.register("$listItem", { allowAttributes: LIST_BASE_ATTRIBUTES });
    if (multiBlock) {
      model.schema.extend("$container", { allowAttributesOf: "$listItem" });
      model.schema.extend("$block", { allowAttributesOf: "$listItem" });
      model.schema.extend("$blockObject", { allowAttributesOf: "$listItem" });
    } else {
      model.schema.register("listItem", {
        inheritAllFrom: "$block",
        allowAttributesOf: "$listItem"
      });
    }
    for (const attribute of LIST_BASE_ATTRIBUTES) {
      model.schema.setAttributeProperties(attribute, {
        copyOnReplace: true
      });
    }
    editor.commands.add("numberedList", new ListCommand(editor, "numbered"));
    editor.commands.add("bulletedList", new ListCommand(editor, "bulleted"));
    editor.commands.add("indentList", new ListIndentCommand(editor, "forward"));
    editor.commands.add("outdentList", new ListIndentCommand(editor, "backward"));
    editor.commands.add("splitListItemBefore", new ListSplitCommand(editor, "before"));
    editor.commands.add("splitListItemAfter", new ListSplitCommand(editor, "after"));
    if (multiBlock) {
      editor.commands.add("mergeListItemBackward", new ListMergeCommand(editor, "backward"));
      editor.commands.add("mergeListItemForward", new ListMergeCommand(editor, "forward"));
    }
    this._setupDeleteIntegration();
    this._setupEnterIntegration();
    this._setupTabIntegration();
    this._setupClipboardIntegration();
  }
  /**
   * @inheritDoc
   */
  afterInit() {
    const editor = this.editor;
    const commands = editor.commands;
    const indent = commands.get("indent");
    const outdent = commands.get("outdent");
    if (indent) {
      indent.registerChildCommand(commands.get("indentList"), { priority: "high" });
    }
    if (outdent) {
      outdent.registerChildCommand(commands.get("outdentList"), { priority: "lowest" });
    }
    this._setupModelPostFixing();
    this._setupConversion();
  }
  /**
   * Registers a downcast strategy.
   *
   * **Note**: Strategies must be registered in the `Plugin#init()` phase so that it can be applied
   * in the `ListEditing#afterInit()`.
   *
   * @param strategy The downcast strategy to register.
   */
  registerDowncastStrategy(strategy) {
    this._downcastStrategies.push(strategy);
  }
  /**
   * Returns list of model attribute names that should affect downcast conversion.
   */
  getListAttributeNames() {
    return [
      ...LIST_BASE_ATTRIBUTES,
      ...this._downcastStrategies.map((strategy) => strategy.attributeName)
    ];
  }
  /**
   * Attaches the listener to the {@link module:engine/view/document~Document#event:delete} event and handles backspace/delete
   * keys in and around document lists.
   */
  _setupDeleteIntegration() {
    const editor = this.editor;
    const mergeBackwardCommand = editor.commands.get("mergeListItemBackward");
    const mergeForwardCommand = editor.commands.get("mergeListItemForward");
    this.listenTo(editor.editing.view.document, "delete", (evt, data) => {
      const selection = editor.model.document.selection;
      if (getSelectedBlockObject(editor.model)) {
        return;
      }
      editor.model.change(() => {
        const firstPosition = selection.getFirstPosition();
        if (selection.isCollapsed && data.direction == "backward") {
          if (!firstPosition.isAtStart) {
            return;
          }
          const positionParent = firstPosition.parent;
          if (!isListItemBlock(positionParent)) {
            return;
          }
          const previousBlock = ListWalker.first(positionParent, {
            sameAttributes: "listType",
            sameIndent: true
          });
          if (!previousBlock && positionParent.getAttribute("listIndent") === 0) {
            if (!isLastBlockOfListItem(positionParent)) {
              editor.execute("splitListItemAfter");
            }
            editor.execute("outdentList");
          } else {
            if (!mergeBackwardCommand || !mergeBackwardCommand.isEnabled) {
              return;
            }
            mergeBackwardCommand.execute({
              shouldMergeOnBlocksContentLevel: shouldMergeOnBlocksContentLevel(editor.model, "backward")
            });
          }
          data.preventDefault();
          evt.stop();
        } else {
          if (selection.isCollapsed && !selection.getLastPosition().isAtEnd) {
            return;
          }
          if (!mergeForwardCommand || !mergeForwardCommand.isEnabled) {
            return;
          }
          mergeForwardCommand.execute({
            shouldMergeOnBlocksContentLevel: shouldMergeOnBlocksContentLevel(editor.model, "forward")
          });
          data.preventDefault();
          evt.stop();
        }
      });
    }, { context: "li" });
  }
  /**
   * Attaches a listener to the {@link module:engine/view/document~Document#event:enter} event and handles enter key press
   * in document lists.
   */
  _setupEnterIntegration() {
    const editor = this.editor;
    const model = editor.model;
    const commands = editor.commands;
    const enterCommand = commands.get("enter");
    this.listenTo(editor.editing.view.document, "enter", (evt, data) => {
      const doc = model.document;
      const positionParent = doc.selection.getFirstPosition().parent;
      if (doc.selection.isCollapsed && isListItemBlock(positionParent) && positionParent.isEmpty && !data.isSoft) {
        const isFirstBlock = isFirstBlockOfListItem(positionParent);
        const isLastBlock = isLastBlockOfListItem(positionParent);
        if (isFirstBlock && isLastBlock) {
          editor.execute("outdentList");
          data.preventDefault();
          evt.stop();
        } else if (isFirstBlock && !isLastBlock) {
          editor.execute("splitListItemAfter");
          data.preventDefault();
          evt.stop();
        } else if (isLastBlock) {
          editor.execute("splitListItemBefore");
          data.preventDefault();
          evt.stop();
        }
      }
    }, { context: "li" });
    this.listenTo(enterCommand, "afterExecute", () => {
      const splitCommand = commands.get("splitListItemBefore");
      splitCommand.refresh();
      if (!splitCommand.isEnabled) {
        return;
      }
      const doc = editor.model.document;
      const positionParent = doc.selection.getLastPosition().parent;
      const listItemBlocks = getAllListItemBlocks(positionParent);
      if (listItemBlocks.length === 2) {
        splitCommand.execute();
      }
    });
  }
  /**
   * Attaches a listener to the {@link module:engine/view/document~Document#event:tab} event and handles tab key and tab+shift keys
   * presses in document lists.
   */
  _setupTabIntegration() {
    const editor = this.editor;
    this.listenTo(editor.editing.view.document, "tab", (evt, data) => {
      const commandName = data.shiftKey ? "outdentList" : "indentList";
      const command = this.editor.commands.get(commandName);
      if (command.isEnabled) {
        editor.execute(commandName);
        data.stopPropagation();
        data.preventDefault();
        evt.stop();
      }
    }, { context: "li" });
  }
  /**
   * Registers the conversion helpers for the document-list feature.
   */
  _setupConversion() {
    const editor = this.editor;
    const model = editor.model;
    const attributeNames = this.getListAttributeNames();
    const multiBlock = editor.config.get("list.multiBlock");
    const elementName = multiBlock ? "paragraph" : "listItem";
    editor.conversion.for("upcast").elementToElement({
      view: "li",
      model: (viewElement, { writer }) => writer.createElement(elementName, { listType: "" })
    }).elementToElement({
      view: "p",
      model: (viewElement, { writer }) => {
        if (viewElement.parent && viewElement.parent.is("element", "li")) {
          return writer.createElement(elementName, { listType: "" });
        }
        return null;
      },
      converterPriority: "high"
    }).add((dispatcher) => {
      dispatcher.on("element:li", listItemUpcastConverter());
      dispatcher.on("element:ul", listUpcastCleanList(), { priority: "high" });
      dispatcher.on("element:ol", listUpcastCleanList(), { priority: "high" });
    });
    if (!multiBlock) {
      editor.conversion.for("downcast").elementToElement({
        model: "listItem",
        view: "p"
      });
    }
    editor.conversion.for("editingDowncast").elementToElement({
      model: elementName,
      view: bogusParagraphCreator(attributeNames),
      converterPriority: "high"
    }).add((dispatcher) => {
      dispatcher.on("attribute", listItemDowncastConverter(attributeNames, this._downcastStrategies, model));
    });
    editor.conversion.for("dataDowncast").elementToElement({
      model: elementName,
      view: bogusParagraphCreator(attributeNames, { dataPipeline: true }),
      converterPriority: "high"
    }).add((dispatcher) => {
      dispatcher.on("attribute", listItemDowncastConverter(attributeNames, this._downcastStrategies, model, { dataPipeline: true }));
    });
    this.listenTo(model.document, "change:data", reconvertItemsOnDataChange(model, editor.editing, attributeNames, this), { priority: "high" });
    this.on("checkAttributes:item", (evt, { viewElement, modelAttributes }) => {
      if (viewElement.id != modelAttributes.listItemId) {
        evt.return = true;
        evt.stop();
      }
    });
    this.on("checkAttributes:list", (evt, { viewElement, modelAttributes }) => {
      if (viewElement.name != getViewElementNameForListType(modelAttributes.listType) || viewElement.id != getViewElementIdForListType(modelAttributes.listType, modelAttributes.listIndent)) {
        evt.return = true;
        evt.stop();
      }
    });
  }
  /**
   * Registers model post-fixers.
   */
  _setupModelPostFixing() {
    const model = this.editor.model;
    const attributeNames = this.getListAttributeNames();
    model.document.registerPostFixer((writer) => modelChangePostFixer(model, writer, attributeNames, this));
    this.on("postFixer", (evt, { listNodes, writer }) => {
      evt.return = fixListIndents(listNodes, writer) || evt.return;
    }, { priority: "high" });
    this.on("postFixer", (evt, { listNodes, writer, seenIds }) => {
      evt.return = fixListItemIds(listNodes, seenIds, writer) || evt.return;
    }, { priority: "high" });
  }
  /**
   * Integrates the feature with the clipboard via {@link module:engine/model/model~Model#insertContent} and
   * {@link module:engine/model/model~Model#getSelectedContent}.
   */
  _setupClipboardIntegration() {
    const model = this.editor.model;
    const clipboardPipeline = this.editor.plugins.get("ClipboardPipeline");
    this.listenTo(model, "insertContent", createModelIndentPasteFixer(model), { priority: "high" });
    this.listenTo(clipboardPipeline, "outputTransformation", (evt, data) => {
      model.change((writer) => {
        const allContentChildren = Array.from(data.content.getChildren());
        const lastItem = allContentChildren[allContentChildren.length - 1];
        if (allContentChildren.length > 1 && lastItem.is("element") && lastItem.isEmpty) {
          const contentChildrenExceptLastItem = allContentChildren.slice(0, -1);
          if (contentChildrenExceptLastItem.every(isListItemBlock)) {
            writer.remove(lastItem);
          }
        }
        if (data.method == "copy" || data.method == "cut") {
          const allChildren = Array.from(data.content.getChildren());
          const isSingleListItemSelected = isSingleListItem(allChildren);
          if (isSingleListItemSelected) {
            removeListAttributes(allChildren, writer);
          }
        }
      });
    });
  }
};
function modelChangePostFixer(model, writer, attributeNames, listEditing) {
  const changes = model.document.differ.getChanges();
  const itemToListHead = /* @__PURE__ */ new Map();
  const multiBlock = listEditing.editor.config.get("list.multiBlock");
  let applied = false;
  for (const entry of changes) {
    if (entry.type == "insert" && entry.name != "$text") {
      const item = entry.position.nodeAfter;
      if (!model.schema.checkAttribute(item, "listItemId")) {
        for (const attributeName of Array.from(item.getAttributeKeys())) {
          if (attributeNames.includes(attributeName)) {
            writer.removeAttribute(attributeName, item);
            applied = true;
          }
        }
      }
      findAndAddListHeadToMap(entry.position, itemToListHead);
      if (!entry.attributes.has("listItemId")) {
        findAndAddListHeadToMap(entry.position.getShiftedBy(entry.length), itemToListHead);
      }
      for (const { item: innerItem, previousPosition } of model.createRangeIn(item)) {
        if (isListItemBlock(innerItem)) {
          findAndAddListHeadToMap(previousPosition, itemToListHead);
        }
      }
    } else if (entry.type == "remove") {
      findAndAddListHeadToMap(entry.position, itemToListHead);
    } else if (entry.type == "attribute" && attributeNames.includes(entry.attributeKey)) {
      findAndAddListHeadToMap(entry.range.start, itemToListHead);
      if (entry.attributeNewValue === null) {
        findAndAddListHeadToMap(entry.range.start.getShiftedBy(1), itemToListHead);
      }
    }
    if (!multiBlock && entry.type == "attribute" && LIST_BASE_ATTRIBUTES.includes(entry.attributeKey)) {
      const element = entry.range.start.nodeAfter;
      if (entry.attributeNewValue === null && element && element.is("element", "listItem")) {
        writer.rename(element, "paragraph");
        applied = true;
      } else if (entry.attributeOldValue === null && element && element.is("element") && element.name != "listItem") {
        writer.rename(element, "listItem");
        applied = true;
      }
    }
  }
  const seenIds = /* @__PURE__ */ new Set();
  for (const listHead of itemToListHead.values()) {
    applied = listEditing.fire("postFixer", {
      listNodes: new ListBlocksIterable(listHead),
      listHead,
      writer,
      seenIds
    }) || applied;
  }
  return applied;
}
function createModelIndentPasteFixer(model) {
  return (evt, [content, selectable]) => {
    const items = content.is("documentFragment") ? Array.from(content.getChildren()) : [content];
    if (!items.length) {
      return;
    }
    const selection = selectable ? model.createSelection(selectable) : model.document.selection;
    const position = selection.getFirstPosition();
    let refItem;
    if (isListItemBlock(position.parent)) {
      refItem = position.parent;
    } else if (isListItemBlock(position.nodeBefore)) {
      refItem = position.nodeBefore;
    } else {
      return;
    }
    model.change((writer) => {
      const refType = refItem.getAttribute("listType");
      const refIndent = refItem.getAttribute("listIndent");
      const firstElementIndent = items[0].getAttribute("listIndent") || 0;
      const indentDiff = Math.max(refIndent - firstElementIndent, 0);
      for (const item of items) {
        const isListItem = isListItemBlock(item);
        if (refItem.is("element", "listItem") && item.is("element", "paragraph")) {
          writer.rename(item, "listItem");
        }
        writer.setAttributes({
          listIndent: (isListItem ? item.getAttribute("listIndent") : 0) + indentDiff,
          listItemId: isListItem ? item.getAttribute("listItemId") : ListItemUid.next(),
          listType: refType
        }, item);
      }
    });
  };
}
function shouldMergeOnBlocksContentLevel(model, direction) {
  const selection = model.document.selection;
  if (!selection.isCollapsed) {
    return !getSelectedBlockObject(model);
  }
  if (direction === "forward") {
    return true;
  }
  const firstPosition = selection.getFirstPosition();
  const positionParent = firstPosition.parent;
  const previousSibling = positionParent.previousSibling;
  if (model.schema.isObject(previousSibling)) {
    return false;
  }
  if (previousSibling.isEmpty) {
    return true;
  }
  return isSingleListItem([positionParent, previousSibling]);
}

// node_modules/@ckeditor/ckeditor5-list/src/list/utils.js
function createUIComponent(editor, commandName, label, icon) {
  editor.ui.componentFactory.add(commandName, (locale) => {
    const command = editor.commands.get(commandName);
    const buttonView = new ButtonView(locale);
    buttonView.set({
      label,
      icon,
      tooltip: true,
      isToggleable: true
    });
    buttonView.bind("isOn", "isEnabled").to(command, "value", "isEnabled");
    buttonView.on("execute", () => {
      editor.execute(commandName);
      editor.editing.view.focus();
    });
    return buttonView;
  });
}

// node_modules/@ckeditor/ckeditor5-list/src/list/listui.js
var ListUI = class extends Plugin {
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "ListUI";
  }
  /**
   * @inheritDoc
   */
  init() {
    const t = this.editor.t;
    createUIComponent(this.editor, "numberedList", t("Numbered List"), icons.numberedList);
    createUIComponent(this.editor, "bulletedList", t("Bulleted List"), icons.bulletedList);
  }
};

// node_modules/@ckeditor/ckeditor5-list/src/list.js
var List = class extends Plugin {
  /**
   * @inheritDoc
   */
  static get requires() {
    return [ListEditing, ListUI];
  }
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "List";
  }
};
export {
  List as default
};
/*! Bundled license information:

@ckeditor/ckeditor5-list/src/list/utils/listwalker.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-list/src/list/utils/model.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-list/src/list/listindentcommand.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-list/src/list/listcommand.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-list/src/list/listmergecommand.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-list/src/list/listsplitcommand.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-list/src/list/listutils.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-list/src/list/utils/view.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-list/src/list/utils/postfixers.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-list/src/list/converters.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-list/src/list/listediting.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-list/src/list/utils.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-list/src/list/listui.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-list/src/list.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)
*/
//# sourceMappingURL=@ckeditor_ckeditor5-list_src_list.js.map
